import type { ReactNode } from 'react';
import React from 'react';

// Components
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import type { PositionType } from '@atlaskit/tooltip/types';

import type { ShortcutListenerProps } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';

interface Props {
	children: ReactNode;
	/**
	 * The content of the tooltip. Will be displayed above the shortcut.
	 * This is required as the Atlaskit tooltip requires some tooltip content to go along with the shortcut.
	 */
	description: string;
	shortcutAccelerator: string;
	shortcutAllowInDialog?: boolean;
	shortcutListener: ShortcutListenerProps['listener'] | null;
	isDisabled?: boolean;
	position: PositionType;
	isScreenReaderAnnouncementDisabled?: boolean;
}

export const TooltipWithShortcut = ({
	children,
	description,
	shortcutAccelerator,
	shortcutAllowInDialog,
	shortcutListener,
	isDisabled = false,
	...otherTooltipProps
}: Props) => {
	const shortcutStrings = useShortcutStrings({ shortcut: shortcutAccelerator });

	return isDisabled ? (
		children
	) : (
		<AkTooltip content={description} shortcut={shortcutStrings} {...otherTooltipProps}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
			<span style={{ display: 'inline-block' }}>
				{children}
				{!shortcutListener ? null : (
					<ShortcutListener
						accelerator={shortcutAccelerator}
						allowInDialog={shortcutAllowInDialog}
						listener={shortcutListener}
					/>
				)}
			</span>
		</AkTooltip>
	);
};
